import React, { useState, useEffect } from "react";
import { createPortal } from "react-dom";
import { Dialog } from "primereact/dialog";
import { Dropdown } from "primereact/dropdown";
import { InputNumber } from "primereact/inputnumber";
import { Button } from "primereact/button";
import { SelectButton } from "primereact/selectbutton";
import { Printer as PrinterIcon, Loader2, MonitorSmartphone, Network } from "lucide-react";
import { useLabelStore } from "../../../store/labelStore";
import { usePrinterStore } from "../../../store/printerStore";
import { usePreferenceStore } from "../../../store/preferenceStore";
import { LabelTemplate } from "../../../types/label";
import { Printer } from "../../../types/printer";
import { WarehouseTask } from "../../../types/warehouse";
import { PrintableLabel } from "../../../components/designer/PrintableLabel";
import { CanvasElement } from "../../../components/designer/types";
import { toast } from "react-toastify";

interface WarehouseTaskPrintDialogProps {
    isOpen: boolean;
    onClose: () => void;
    task: WarehouseTask | null;
    printCategory: "PICKING" | "PACKING";
}

export const WarehouseTaskPrintDialog: React.FC<WarehouseTaskPrintDialogProps> = ({
    isOpen, onClose, task, printCategory
}) => {
    const { fetchDataDynamic: fetchTemplates } = useLabelStore();
    const { fetchDataDynamic: fetchPrinters, printPreview, getPreviewJson } = usePrinterStore();
    const { printMethod, setPrintMethod } = usePreferenceStore();

    const [templates, setTemplates] = useState<LabelTemplate[]>([]);
    const [printers, setPrinters] = useState<Printer[]>([]);

    const [selectedTemplate, setSelectedTemplate] = useState<LabelTemplate | null>(null);
    const [selectedPrinter, setSelectedPrinter] = useState<Printer | null>(null);

    const [copies, setCopies] = useState<number>(1);

    // Preview states
    const [elements, setElements] = useState<CanvasElement[]>([]);
    const [widthMm, setWidthMm] = useState(100);
    const [heightMm, setHeightMm] = useState(100);
    const [isPrinting, setIsPrinting] = useState(false);
    
    // Dynamic payload from API
    const [previewData, setPreviewData] = useState<any>({});

    const title = printCategory === "PICKING" ? "Toplayıcı Etiketi Yazdır" : "Paketleyici Etiketi Yazdır";

    useEffect(() => {
        if (isOpen) {
            loadTemplatesAndPrinters();
            setCopies(1);
        }
    }, [isOpen, printCategory]);

    useEffect(() => {
        if (selectedTemplate && selectedPrinter && task) {
            getPreviewJson({
                category: printCategory,
                templateId: selectedTemplate.id,
                printerId: selectedPrinter.id,
                entityId: task.id
            }).then((res) => {
                if (res && res.success) {
                    setElements(res.data || []);
                    setWidthMm(res.widthMm || 100);
                    setHeightMm(res.heightMm || 100);
                }
            }).catch(err => {
                console.error("Önizleme yüklenirken hata oluştu", err);
                setElements([]);
            });
        } else if (selectedTemplate) {
            // Fallback for when printer is not selected but template is
            const variant = selectedTemplate.variants && selectedTemplate.variants.length > 0
                ? selectedTemplate.variants[0]
                : null;
            if (variant) {
                setWidthMm(variant.widthMm || 100);
                setHeightMm(variant.heightMm || 100);
            } else if (selectedTemplate.designJson) {
                try {
                    const parsed = typeof selectedTemplate.designJson === 'string' ? JSON.parse(selectedTemplate.designJson) : selectedTemplate.designJson;
                    setWidthMm(parsed.width || (selectedTemplate as any).width || 100);
                    setHeightMm(parsed.height || (selectedTemplate as any).height || 100);
                } catch (e) {
                    setWidthMm((selectedTemplate as any).width || 100);
                    setHeightMm((selectedTemplate as any).height || 100);
                }
            }
        }
    }, [selectedTemplate, selectedPrinter, task, printCategory]);

    const loadTemplatesAndPrinters = async () => {
        try {
            const tempRes = await fetchTemplates({
                filter: { logic: "and", filters: [{ field: "category", operator: "eq", value: printCategory }] },
                pageIndex: 1, pageSize: 100
            });
            setTemplates(tempRes.items || []);

            const printRes = await fetchPrinters({ pageIndex: 1, pageSize: 100 });
            setPrinters(printRes.items || []);

            // Set defaults if available
            if (tempRes.items && tempRes.items.length > 0) setSelectedTemplate(tempRes.items[0]);
            if (printRes.items && printRes.items.length > 0) setSelectedPrinter(printRes.items[0]);
        } catch (error) {
            console.error("Yükleme hatası:", error);
            toast.error("Şablonlar veya yazıcılar yüklenemedi.");
        }
    };

    const handlePrintNetwork = async () => {
        if (!selectedTemplate || !task) {
            toast.error("Lütfen şablon ve görev seçtiğinizden emin olun.");
            return;
        }

        if (printMethod === 'local') {
            setIsPrinting(true);
            setTimeout(() => {
                window.print();
                setIsPrinting(false);
                onClose();
            }, 300);
            return;
        }

        if (!selectedPrinter) {
            toast.error("Lütfen yazıcı seçtiğinizden emin olun.");
            return;
        }

        if (!selectedPrinter.ipAddress) {
            toast.error("Seçili yazıcının IP adresi bulunmuyor. Lütfen bir ağ yazıcısı seçin.");
            return;
        }

        setIsPrinting(true);
        try {
            const resData = await printPreview({
                category: printCategory,
                templateId: Number(selectedTemplate.id),
                printerId: Number(selectedPrinter.id),
                entityId: Number(task.id),
            });

            const printCode = resData?.data || resData?.code;
            if (!printCode) throw new Error("Yazdırma kodu alınamadı.");

            let finalCode = "";
            for (let i = 0; i < copies; i++) {
                finalCode += printCode + "\n";
            }

            // Yazıcıya yerel ağdan doğrudan (raw) veri gönder
            // Yazıcı lokal ağda olduğu için API (cloud) erişemez.
            await fetch(`http://${selectedPrinter.ipAddress}:9100/`, {
                method: 'POST',
                body: finalCode,
                mode: 'no-cors'
            });

            toast.success(`Yazdırma isteği ${selectedPrinter.ipAddress} adresine başarıyla gönderildi.`);

        } catch (error: any) {
            console.error("Yazdırma hatası:", error);
            toast.error(error.response?.data?.message || "Yazdırma işlemi başarısız oldu.");
        } finally {
            setIsPrinting(false);
            onClose();
        }
    };

    // Dynamic payload now handles this, fallback handled at fetch or here via empty mapping if needed
    // previewData already loaded from API in useEffect

    const printItems = [];
    for (let i = 0; i < copies; i++) {
        printItems.push(previewData);
    }

    const printOptions = [
        { label: 'Ağ Yazıcısı', value: 'network' },
        { label: 'Yerel Yazıcı', value: 'local' }
    ];

    // Önizleme hesaplamaları
    const previewBoxWidth = 350;
    const previewBoxHeight = 350;
    const labelWidthPx = widthMm * 3.78;
    const labelHeightPx = heightMm * 3.78;
    const scaleX = previewBoxWidth / labelWidthPx;
    const scaleY = previewBoxHeight / labelHeightPx;
    const scale = Math.min(scaleX, scaleY, 1) * 0.95;

    return (
        <>
            <Dialog
                visible={isOpen}
                onHide={onClose}
                header={title}
                className="w-full md:w-[900px] lg:w-[1000px]"
                modal
            >
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6 p-2">
                    <div className="flex flex-col gap-4">
                        <div className="grid grid-cols-1 gap-4 mb-4">
                            <div className="flex flex-col gap-2">
                                <label className="text-sm font-semibold">Yazdırma Yöntemi</label>
                                <div className="flex bg-bgAlt p-1 rounded-xl w-full border border-borderColor">
                                    <button
                                        type="button"
                                        onClick={() => setPrintMethod('network')}
                                        className={`flex-1 flex items-center justify-center py-2 px-3 rounded-lg text-sm font-bold transition-all ${printMethod === 'network' ? 'bg-card shadow-sm text-primary border-borderColor border' : 'text-text/60 hover:text-text hover:bg-inputHover'}`}
                                    >
                                        <Network className="w-4 h-4 mr-2" />
                                        Ağ Yazıcısı
                                    </button>
                                    <button
                                        type="button"
                                        onClick={() => setPrintMethod('local')}
                                        className={`flex-1 flex items-center justify-center py-2 px-3 rounded-lg text-sm font-bold transition-all ${printMethod === 'local' ? 'bg-card shadow-sm text-primary border-borderColor border' : 'text-text/60 hover:text-text hover:bg-inputHover'}`}
                                    >
                                        <MonitorSmartphone className="w-4 h-4 mr-2" />
                                        Yerel Yazıcı (Tarayıcı)
                                    </button>
                                </div>
                            </div>
                        </div>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                            <div className="flex flex-col gap-2">
                                <label className="text-sm font-semibold">Yazıcı</label>
                                <Dropdown
                                    value={selectedPrinter}
                                    options={printers}
                                    onChange={(e) => setSelectedPrinter(e.value)}
                                    optionLabel="name"
                                    placeholder="Yazıcı Seçiniz"
                                    className="w-full"
                                    disabled={printMethod === 'local'}
                                />
                            </div>
                            <div className="flex flex-col gap-2">
                                <label className="text-sm font-semibold">Etiket Şablonu</label>
                                <Dropdown
                                    value={selectedTemplate}
                                    options={templates}
                                    onChange={(e) => setSelectedTemplate(e.value)}
                                    optionLabel="name"
                                    placeholder="Şablon Seçiniz"
                                    className="w-full"
                                />
                            </div>
                        </div>

                        <div className="flex flex-col gap-2 border-t pt-4 mt-2">
                            <label className="text-sm font-semibold text-primary">Yazdırma Miktarı (Kopya Sayısı)</label>
                            <InputNumber
                                value={copies}
                                onValueChange={(e) => setCopies(e.value || 1)}
                                min={1}
                                max={1000}
                                className="w-full"
                                showButtons
                            />
                        </div>
                    </div>

                    <div className="flex flex-col border-l pl-6 border-gray-200">
                        <label className="text-sm font-semibold text-gray-500 mb-2 text-center w-full">ETİKET ÖNİZLEME</label>

                        <div className="flex flex-col items-center flex-grow justify-center bg-gray-50 rounded-xl p-4 border border-dashed border-gray-300">
                            {elements.length > 0 ? (
                                <div
                                    className="border-2 border-black bg-white flex justify-center items-center overflow-hidden shadow-sm"
                                    style={{ width: previewBoxWidth, height: previewBoxHeight, position: 'relative' }}
                                >
                                    <div style={{ transform: `scale(${scale})`, transformOrigin: 'center center' }}>
                                        <PrintableLabel
                                            elements={elements}
                                            widthMm={widthMm}
                                            heightMm={heightMm}
                                            data={previewData}
                                        />
                                    </div>
                                </div>
                            ) : (
                                <div className="text-gray-400 text-sm text-center">Önizleme için şablon seçin</div>
                            )}
                            {elements.length > 0 && (
                                <div className="mt-5 font-bold text-orange-500 text-base">
                                    {copies} Adet Etiket Yazdırılacak
                                </div>
                            )}
                        </div>

                        <div className="flex mt-6 w-full">
                            <Button
                                label={isPrinting ? "YAZDIRILIYOR..." : "YAZDIR"}
                                icon={isPrinting ? <Loader2 className="w-5 h-5 mr-2 animate-spin" /> : <PrinterIcon className="w-5 h-5 mr-2" />}
                                onClick={handlePrintNetwork}
                                disabled={isPrinting || !selectedTemplate || (printMethod === 'network' && !selectedPrinter) || elements.length === 0}
                                className="bg-black hover:bg-gray-800 border-none text-white w-full py-4 text-lg font-bold rounded-xl flex justify-center items-center"
                            />
                        </div>
                    </div>
                </div>
            </Dialog>

            {isPrinting && createPortal(
                <div className="print-container">
                    <style>{`
                    @media print {
                        @page {
                            size: ${widthMm}mm ${heightMm}mm;
                            margin: 0;
                        }
                    }
                `}</style>
                    {printItems.map((data, index) => (
                        <PrintableLabel
                            key={`print-${index}`}
                            elements={elements}
                            widthMm={widthMm}
                            heightMm={heightMm}
                            data={data}
                        />
                    ))}
                </div>,
                document.body
            )}
        </>
    );
};
